import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import type { Category, CategoryInput } from "@/types/models";
import { optionalSlugSchema, slugInputValue } from "@/lib/slug";

const categorySchema = z.object({
  name: z.string().min(1, "Nome é obrigatório"),
  slug: optionalSlugSchema,
  active: z.boolean(),
});

export type CategoryFormValues = z.infer<typeof categorySchema>;

const defaultValues: CategoryFormValues = {
  name: "",
  slug: "",
  active: true,
};

function toCategoryInput(values: CategoryFormValues, id?: string): CategoryInput {
  const input: CategoryInput = {
    id,
    name: values.name.trim(),
    active: values.active,
  };
  const slug = slugInputValue(values.slug);
  if (slug) input.slug = slug;
  return input;
}

export interface CategoryFormProps {
  selectedCategory?: Category | null;
  saving?: boolean;
  onSubmit: (input: CategoryInput) => void;
  onCancelEdit?: () => void;
}

export function CategoryForm({
  selectedCategory,
  saving = false,
  onSubmit,
  onCancelEdit,
}: CategoryFormProps) {
  const [actionError, setActionError] = useState<string | null>(null);
  const isEditMode = Boolean(selectedCategory);

  const form = useForm<CategoryFormValues>({
    resolver: zodResolver(categorySchema),
    defaultValues,
  });

  useEffect(() => {
    if (!selectedCategory) {
      form.reset(defaultValues);
      return;
    }

    form.reset({
      name: selectedCategory.name,
      slug: selectedCategory.slug,
      active: selectedCategory.active,
    });
  }, [selectedCategory, form]);

  const handleSubmit = (values: CategoryFormValues) => {
    setActionError(null);
    onSubmit(toCategoryInput(values, selectedCategory?.id));
  };

  return (
    <section className="border border-primary-200 dark:border-primary-800 p-8">
      <h2 className="text-xl font-light text-primary-900 dark:text-white mb-6">
        {isEditMode ? "Editar categoria" : "Nova categoria"}
      </h2>

      {actionError && (
        <Alert variant="destructive" className="mb-6">
          <AlertTitle>Erro</AlertTitle>
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6 max-w-md">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nome</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Ex: Eletrônicos" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="slug"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Slug (opcional)</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Gerado automaticamente a partir do nome" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="active"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center gap-3 space-y-0">
                <FormControl>
                  <input
                    type="checkbox"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    className="h-4 w-4 rounded border-primary-300"
                  />
                </FormControl>
                <FormLabel className="font-normal">Categoria ativa</FormLabel>
              </FormItem>
            )}
          />

          <div className="flex flex-wrap gap-3">
            <Button type="submit" disabled={saving}>
              {saving ? "Salvando…" : isEditMode ? "Salvar alterações" : "Criar categoria"}
            </Button>
            {isEditMode && onCancelEdit && (
              <Button type="button" variant="outline" onClick={onCancelEdit}>
                Cancelar
              </Button>
            )}
          </div>
        </form>
      </Form>
    </section>
  );
}
